/**
 * Application Constants
 *
 * Costanti globali dell'applicazione.
 * Centralizza valori riutilizzabili per evitare magic numbers/strings.
 */

/**
 * Configurazione API
 */
export const API_CONFIG = {
  /** Timeout richieste in millisecondi */
  TIMEOUT: 30_000,
  /** Numero massimo di retry per richieste fallite */
  MAX_RETRIES: 3,
  /** Delay base per retry exponential backoff (ms) */
  RETRY_DELAY: 1000,
  /** Headers comuni per tutte le richieste */
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
  },
} as const;

/**
 * Configurazione React Query
 */
export const QUERY_CONFIG = {
  /** Tempo di stale in millisecondi (5 minuti) */
  STALE_TIME: 5 * 60 * 1000,
  /** Tempo di stale per dati dinamici che cambiano frequentemente (30 secondi) */
  DYNAMIC_STALE_TIME: 30 * 1000,
  /** Tempo di cache in millisecondi (30 minuti) */
  CACHE_TIME: 30 * 60 * 1000,
  /** Numero di retry per query fallite */
  RETRY_COUNT: 2,
  /** Refetch su window focus */
  REFETCH_ON_FOCUS: true,
  /** Refetch su reconnect */
  REFETCH_ON_RECONNECT: true,
} as const;

/**
 * Costanti UI
 */
export const UI_CONFIG = {
  /** Durata animazioni in millisecondi */
  ANIMATION_DURATION: 200,
  /** Delay per debounce input */
  DEBOUNCE_DELAY: 300,
  /** Elementi per pagina default */
  PAGE_SIZE: 10,
  /** Durata toast notification in millisecondi */
  TOAST_DURATION: 5000,
} as const;

/**
 * Validazione form
 */
export const VALIDATION = {
  /** Lunghezza minima password */
  MIN_PASSWORD_LENGTH: 8,
  /** Lunghezza massima commento */
  MAX_COMMENT_LENGTH: 1000,
  /** Lunghezza massima nome */
  MAX_NAME_LENGTH: 100,
  /** Pattern email */
  EMAIL_PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  /** Pattern telefono italiano */
  PHONE_PATTERN: /^(\+39)?\s?[0-9]{10}$/,
} as const;

/**
 * Storage keys per localStorage/sessionStorage
 */
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'capviaggi_auth_token',
  REFRESH_TOKEN: 'capviaggi_refresh_token',
  TOKEN_EXPIRES_AT: 'capviaggi_token_expires_at',
  AUTH_STATUS: 'capviaggi_auth',
  USER_DATA: 'capviaggi_user',
  USER_PREFERENCES: 'capviaggi_preferences',
  THEME: 'capviaggi_theme',
  LOCALE: 'capviaggi_locale',
} as const;

/**
 * Configurazione Token Refresh
 */
export const TOKEN_REFRESH_CONFIG = {
  /** Tempo prima della scadenza per effettuare il refresh (5 minuti in ms) */
  REFRESH_THRESHOLD: 5 * 60 * 1000,
  /** Intervallo di check per il refresh automatico (1 minuto in ms) */
  REFRESH_CHECK_INTERVAL: 60 * 1000,
} as const;

/**
 * Route paths dell'applicazione
 */
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  DASHBOARD: '/dashboard',
  SURVEYS: '/surveys',
  SURVEY_DETAIL: '/surveys/:id',
  TRIPS: '/trips',
  TRIP_DETAIL: '/trips/:id',
  SETTINGS: '/settings',
} as const;

/**
 * Rating labels per la scala di valutazione
 */
export const RATING_LABELS = {
  1: 'Pessimo',
  2: 'Scarso',
  3: 'Sufficiente',
  4: 'Buono',
  5: 'Eccellente',
} as const;

/**
 * Configurazione questionari e raccolta feedback
 */
export const SURVEY_CONFIG = {
  /** Giorni di validità questionari dopo il rientro del viaggio */
  VALIDITY_DAYS: 30,
  /** Soglia giorni rimanenti per stato "in scadenza" (warning) */
  EXPIRING_THRESHOLD_DAYS: 10,
  /** Numero massimo di reminder per questionario */
  MAX_REMINDERS: 3,
  /** Ore minime tra un reminder e l'altro */
  MIN_HOURS_BETWEEN_REMINDERS: 24,
} as const;
